import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggle", "settings", "container", "fileInput", "entry", "number", "position", "destroy", "empty"]
  static values = { index: { type: Number, default: 0 } }

  connect() {
    this.draggedElement = null
  }

  toggleSettings() {
    if (this.hasSettingsTarget) {
      this.settingsTarget.classList.toggle("hidden", !this.toggleTarget.checked)
    }
  }

  openFilePicker() {
    this.fileInputTarget.click()
  }

  filesSelected(event) {
    const files = Array.from(event.target.files)
    files.forEach(file => this.addSlide(file))
    // Reset input so the same files can be selected again
    this.fileInputTarget.value = ""
    this.updateEmptyState()
  }

  addSlide(file) {
    const index = this.indexValue
    this.indexValue = index + 1

    const card = document.createElement("div")
    card.classList.add("slideshow-slide-card")
    card.draggable = true
    card.dataset.slideshowEditorTarget = "entry"
    card.dataset.action = "dragstart->slideshow-editor#dragstart dragover->slideshow-editor#dragover dragenter->slideshow-editor#dragenter dragleave->slideshow-editor#dragleave drop->slideshow-editor#drop dragend->slideshow-editor#dragend"

    card.innerHTML = `
      <div class="slideshow-slide-preview">
        <img src="" alt="New slide">
        <div class="slideshow-slide-overlay">
          <button type="button"
                  class="slideshow-slide-remove"
                  data-action="click->slideshow-editor#removeNew"
                  title="${this.fileInputTarget.closest('[data-controller]').querySelector('.slideshow-slide-remove')?.title || 'Eliminar'}">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
      <div class="slideshow-slide-info">
        <span class="slideshow-slide-number" data-slideshow-editor-target="number">${this.entryTargets.length + 1}</span>
        <svg class="slideshow-slide-drag w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
        </svg>
      </div>
      <input type="hidden" name="event[slides_attributes][${index}][position]" value="${index}" data-slideshow-editor-target="position">
      <input type="hidden" name="event[slides_attributes][${index}][enabled]" value="1">
    `

    // Create a proper file input for the new slide
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.name = `event[slides_attributes][${index}][image]`
    fileInput.classList.add("hidden")
    // Transfer the file via DataTransfer
    const dt = new DataTransfer()
    dt.items.add(file)
    fileInput.files = dt.files
    card.appendChild(fileInput)

    // Show preview
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = card.querySelector("img")
      if (img) img.src = e.target.result
    }
    reader.readAsDataURL(file)

    this.containerTarget.appendChild(card)
    this.updatePositions()
  }

  removeExisting(event) {
    event.preventDefault()
    event.stopPropagation()
    const card = event.target.closest("[data-slideshow-editor-target='entry']")
    if (!card) return

    // Mark for destruction
    const destroyInput = card.querySelector("input[name*='[_destroy]']")
    if (destroyInput) {
      destroyInput.value = "1"
      card.classList.add("hidden")
    }
    this.updatePositions()
    this.updateEmptyState()
  }

  removeNew(event) {
    event.preventDefault()
    event.stopPropagation()
    const card = event.target.closest("[data-slideshow-editor-target='entry']")
    if (!card) return

    card.remove()
    this.updatePositions()
    this.updateEmptyState()
  }

  // Drag & Drop reordering
  dragstart(event) {
    this.draggedElement = event.target.closest("[data-slideshow-editor-target='entry']")
    if (!this.draggedElement) return
    this.draggedElement.classList.add("slideshow-dragging")
    event.dataTransfer.effectAllowed = "move"
  }

  dragover(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  dragenter(event) {
    event.preventDefault()
    const entry = event.target.closest("[data-slideshow-editor-target='entry']")
    if (entry && entry !== this.draggedElement) {
      entry.classList.add("slideshow-drag-over")
    }
  }

  dragleave(event) {
    const entry = event.target.closest("[data-slideshow-editor-target='entry']")
    if (entry) {
      entry.classList.remove("slideshow-drag-over")
    }
  }

  drop(event) {
    event.preventDefault()
    const target = event.target.closest("[data-slideshow-editor-target='entry']")
    if (!target || !this.draggedElement || target === this.draggedElement) return

    target.classList.remove("slideshow-drag-over")

    // Determine insert position
    const entries = Array.from(this.containerTarget.children).filter(
      el => el.dataset.slideshowEditorTarget === "entry" && !el.classList.contains("hidden")
    )
    const draggedIdx = entries.indexOf(this.draggedElement)
    const targetIdx = entries.indexOf(target)

    if (draggedIdx < targetIdx) {
      target.after(this.draggedElement)
    } else {
      target.before(this.draggedElement)
    }

    this.updatePositions()
  }

  dragend() {
    if (this.draggedElement) {
      this.draggedElement.classList.remove("slideshow-dragging")
      this.draggedElement = null
    }
    this.entryTargets.forEach(entry => entry.classList.remove("slideshow-drag-over"))
  }

  updatePositions() {
    let visibleIndex = 0
    this.entryTargets.forEach(entry => {
      if (entry.classList.contains("hidden")) return

      const posInput = entry.querySelector("input[name*='[position]']")
      if (posInput) posInput.value = visibleIndex

      const numberEl = entry.querySelector("[data-slideshow-editor-target='number']")
      if (numberEl) numberEl.textContent = visibleIndex + 1

      visibleIndex++
    })
  }

  updateEmptyState() {
    if (!this.hasEmptyTarget) return
    const visibleEntries = this.entryTargets.filter(e => !e.classList.contains("hidden"))
    this.emptyTarget.classList.toggle("hidden", visibleEntries.length > 0)
  }
}
